<template>
  <div>
    <EmptyScriptPlaceholder
      v-if="materialScriptModel.dataLoaded && materialScriptModel.listTotal === 0"
      style="height: calc(100vh - 180px)"
    >
      <template #aiscript>
        <el-button icon="el-icon-magic-stick" @click="$router.push({ name: 'materialscriptcreate' })"> 获取创意灵感 </el-button>
      </template>

      <template #manualscript>
        <el-button type="primary" icon="el-icon-plus" @click="onCreate"> 开始手动创作 </el-button>
      </template>
    </EmptyScriptPlaceholder>

    <template v-else>
      <div class="fitler-form">
        <ElForm class="elementui-common-form-styles" v-highlight @submit.native.prevent label-width="50" inline>
          <ElFormItem>
            <el-input
              v-model="queryForm.keyword"
              placeholder="请输入脚本名称"
              style="width: 220px"
              clearable
              @keyup.enter.native="onQueryAction"
              @clear="onQueryAction"
            >
              <i slot="append" @click="onQueryAction" class="el-icon-search"></i>
            </el-input>
          </ElFormItem>
        </ElForm>

        <div>
          <el-button @click="$router.push({ name: 'materialscriptcreate' })">脚本生成</el-button>
          <el-button @click="onCreate" type="primary">新建脚本</el-button>
        </div>
      </div>

      <Viewport v-model="height">
        <el-table
          class="common-el-table-styles table-cell-white-space-normal"
          ref="table"
          :data="materialScriptModel.tableData"
          :height="height - 15"
          v-loading="materialScriptModel.tableLoading"
          border
          stripe
        >
          <el-table-column label="脚本名称" width="100" prop="scriptName"></el-table-column>
          <el-table-column label="分镜标题" width="100" prop="shot_title">
            <template slot-scope="{ row }">
              <div v-html="row.shot_title"></div>
            </template>
          </el-table-column>
          <el-table-column label="画面描述" prop="visual_core">
            <template slot-scope="{ row }">
              <div v-html="row.visual_core"></div>
            </template>
          </el-table-column>
          <el-table-column label="叙事重点" prop="content_narrative_focus">
            <template slot-scope="{ row }">
              <div v-html="row.content_narrative_focus"></div>
            </template>
          </el-table-column>
          <el-table-column label="内容标签" prop="emotion_atmosphere">
            <template slot-scope="{ row }">
              <div v-html="row.emotion_atmosphere"></div>
            </template>
          </el-table-column>
          <el-table-column label="关键元素" prop="key_elements">
            <template slot-scope="{ row }">
              <div v-html="row.key_elements"></div>
            </template>
          </el-table-column>
          <el-table-column show-overflow-tooltip label="最后修改时间" width="140" prop="updateTimeText"></el-table-column>
          <el-table-column label="操作" width="100">
            <template slot-scope="{ row }">
              <el-button type="text" :disabled="row.taskRunCount > 0" @click="onEdit(row)">编辑</el-button>
              <el-button type="text" @click="onRemove(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <el-pagination
          style="text-align: right"
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page.sync="pager.pageIndex"
          :page-size="pager.pageSize"
          :page-sizes="[10, 30, 50]"
          :total="pager.total"
          @current-change="changePageIndex"
          @size-change="changePageSize"
        >
        </el-pagination>
      </Viewport>
    </template>
  </div>
</template>

<script setup>
import moment from 'moment'
import { ref, reactive, onMounted, onActivated } from 'vue'
import { useRouter, useRoute } from 'vue-router/composables'
import { MessageBox } from 'element-ui'
import EmptyScriptPlaceholder from './components/placeholder/index.vue'
import { getMaterialScriptList, removeMaterialScript } from '@/api/material.model.js'
const router = useRouter()
const route = useRoute()

const height = ref(300)
const queryForm = ref({ keyword: '' })
const materialScriptModel = reactive({
  tableData: [],
  listTotal: 0,
  dataLoaded: false,
  tableLoading: false
})
const pager = ref({
  pageIndex: 1,
  pageSize: 10,
  total: 0
})

/**
 * @description: 获取素材列表不带查询参数的总数，因为后端不愿意提供总数，这里的总数用来控制是否显示引导页面
 * @return {*}
 */
const getTotal = async () => {
  const { page_info } = await getMaterialScriptList({ pageIndex: 1, pageSize: 1 })
  return page_info.total_number || 0
}

const separator = '</br></br>'
const getTableData = () => {
  materialScriptModel.tableLoading = true
  getMaterialScriptList({
    ...queryForm.value,
    pageIndex: pager.value.pageIndex,
    pageSize: pager.value.pageSize
  })
    .then((data) => {
      const { page_info, list } = data
      materialScriptModel.tableData =
        list?.map((i) => {
          const shots = i.script.shots

          return {
            ...i,
            shot_title: shots
              .map((shot) => shot.shot_title)
              .filter((i) => i)
              .join('</br>'),
            visual_core: shots
              .map((shot) => shot.visual_core)
              .filter((i) => i)
              .join(separator),
            content_narrative_focus: shots
              .map((shot) => shot.content_narrative_focus)
              .filter((i) => i)
              .join(separator),
            emotion_atmosphere: shots
              .map((shot) => shot.emotion_atmosphere)
              .filter((i) => i)
              .join(separator),
            key_elements: shots
              .map((shot) => shot.key_elements)
              .filter((i) => i)
              .join(separator),
            updateTimeText: moment(i.updateTime).format('YYYY-MM-DD HH:mm:ss')
          }
        }) || []
      pager.value.total = page_info?.total_number || 0
      getTotal().then((totalRows) => {
        materialScriptModel.listTotal = totalRows
        materialScriptModel.dataLoaded = true
      })
    })
    .finally(() => {
      materialScriptModel.tableLoading = false
    })
}
const onQueryAction = () => {
  pager.value.pageIndex = 1
  getTableData()
}
const changePageIndex = (index) => {
  pager.value.pageIndex = index
  getTableData()
}
const changePageSize = (size) => {
  pager.value.pageSize = size
  onQueryAction()
}

onMounted(onQueryAction)
onActivated(() => {
  const { refresh } = route.params
  if (refresh) {
    getTableData()
  }
})

const onCreate = () => {
  router.push({ name: 'materialscriptedit', params: { scriptId: `${Date.now()}` } })
}
const onEdit = (row) => {
  router.push({ name: 'materialscriptedit', params: { scriptId: `${row.scriptId}` } })
}
const onRemove = (row) => {
  MessageBox.confirm(`确认删除[${row.scriptName}]脚本？`, '确认', { type: 'warning' }).then(() => {
    removeMaterialScript({ scriptId: row.scriptId }).then(() => {
      getTableData()
    })
  })
}
</script>

<style scoped lang="scss">
.fitler-form {
  padding-top: 8px;
  display: flex;
  justify-content: space-between;
}
</style>
